import { z } from "zod"

import {
  platformCommandEnvelopeV1Schema,
} from "./platform-command-v1.js"
import {
  platformCollectionsStatusV1Schema,
  platformSubscriptionStatusV1Schema,
} from "./platform-billing-reconciliation-v1.js"
import { platformQueryEnvelopeV1Schema } from "./tenant-directory-v1.js"
import { tenantIdSchema } from "./identifiers.js"

const moneySchema = z.number().int().min(-1_000_000_000).max(1_000_000_000)
const commercialActionSchema = z.enum([
  "trial_extend",
  "plan_change",
  "invoice_retry",
  "credit_issue",
  "dunning_pause",
  "subscription_cancel",
])

export const platformCommercialFiltersV1Schema = z.object({
  exactTenantId: tenantIdSchema.nullable(),
  subscriptionStates: z.array(platformSubscriptionStatusV1Schema).max(5),
  collectionsStates: z.array(platformCollectionsStatusV1Schema).max(4),
  actionRequiredOnly: z.boolean(),
}).strict()

export const platformCommercialQueryV1Schema = z.object({
  envelope: platformQueryEnvelopeV1Schema.extend({
    queryType: z.literal("PlatformCommercialOperations"),
  }),
  filters: platformCommercialFiltersV1Schema,
  page: z.object({
    limit: z.number().int().min(1).max(100),
    cursor: z.string().min(16).max(512).nullable(),
  }).strict(),
}).strict()

export const platformCommercialItemV1Schema = z.object({
  targetTenantId: tenantIdSchema,
  safeDisplayLabel: z.string().min(1).max(120).nullable(),
  subscriptionVersion: z.number().int().positive(),
  subscriptionStatus: platformSubscriptionStatusV1Schema,
  collectionsStatus: platformCollectionsStatusV1Schema,
  planCode: z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/),
  planRevision: z.number().int().positive(),
  monthlyRecurringRevenueMinor: z.number().int().nonnegative(),
  openInvoiceAmountMinor: z.number().int().nonnegative(),
  creditBalanceMinor: moneySchema,
  currency: z.literal("usd"),
  nextBillingAt: z.iso.datetime({ offset: true }).nullable(),
  trialEndsAt: z.iso.datetime({ offset: true }).nullable(),
  dunningAttemptCount: z.number().int().nonnegative().max(100),
  actionRequired: z.boolean(),
  safeFailureCode: z.string().regex(/^[A-Z][A-Z0-9_]{2,63}$/).nullable(),
  stale: z.boolean(),
  observedAt: z.iso.datetime({ offset: true }),
}).strict()

export const platformCommercialPageV1Schema = z.object({
  responseVersion: z.literal(1),
  generatedAt: z.iso.datetime({ offset: true }),
  partial: z.boolean(),
  summary: z.object({
    activeSubscriptions: z.number().int().nonnegative(),
    trialingSubscriptions: z.number().int().nonnegative(),
    pastDueSubscriptions: z.number().int().nonnegative(),
    restrictedSubscriptions: z.number().int().nonnegative(),
    monthlyRecurringRevenueMinor: z.number().int().nonnegative(),
    openInvoiceAmountMinor: z.number().int().nonnegative(),
    issuedCreditAmountMinor: z.number().int().nonnegative(),
    currency: z.literal("usd"),
  }).strict(),
  items: z.array(platformCommercialItemV1Schema).max(100),
  nextCursor: z.string().min(16).max(512).nullable(),
}).strict()

export const platformCommercialActionRequestV1Schema = z.object({
  action: commercialActionSchema,
  targetTenantId: tenantIdSchema,
  expectedSubscriptionVersion: z.number().int().positive(),
  amountMinor: z.number().int().positive().max(1_000_000_000).nullable(),
  targetPlanCode: z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/).nullable(),
  effectiveAt: z.iso.datetime({ offset: true }),
  reasonCode: z.enum([
    "customer_request",
    "contract_change",
    "service_recovery",
    "collection_exception",
    "billing_correction",
  ]),
}).strict().superRefine((request, context) => {
  if ((request.action === "credit_issue") !== (request.amountMinor !== null)) {
    context.addIssue({
      code: "custom",
      path: ["amountMinor"],
      message: "Only credit issuance carries an amount.",
    })
  }
  if ((request.action === "plan_change") !== (request.targetPlanCode !== null)) {
    context.addIssue({
      code: "custom",
      path: ["targetPlanCode"],
      message: "Only plan changes carry a target plan.",
    })
  }
})

export const platformCommercialActionPreviewV1Schema = z.object({
  responseVersion: z.literal(1),
  previewFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  action: commercialActionSchema,
  targetTenantId: tenantIdSchema,
  expectedSubscriptionVersion: z.number().int().positive(),
  requiresApproval: z.boolean(),
  requiredApprovalCount: z.number().int().min(0).max(2),
  monetaryImpactMinor: moneySchema,
  currency: z.literal("usd"),
  consequence: z.enum([
    "no_access_change",
    "access_expands",
    "access_restricts_non_destructively",
  ]),
  preservesExportSupportHistory: z.literal(true),
  preservesTenantData: z.literal(true),
  expiresAt: z.iso.datetime({ offset: true }),
}).strict()

export const executePlatformCommercialActionV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema.safeExtend({
    commandType: z.literal("ExecutePlatformCommercialAction"),
  }),
  request: platformCommercialActionRequestV1Schema,
  previewFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  previewExpiresAt: z.iso.datetime({ offset: true }),
}).strict().superRefine((command, context) => {
  if (
    command.envelope.target.scope !== "tenant"
    || command.envelope.target.targetTenantId !== command.request.targetTenantId
    || String(command.envelope.target.targetId)
      !== String(command.request.targetTenantId)
    || command.envelope.target.targetType !== "platform_subscription"
    || command.envelope.target.expectedVersion
      !== command.request.expectedSubscriptionVersion
  ) {
    context.addIssue({
      code: "custom",
      path: ["envelope", "target"],
      message: "Commercial command target must match the exact subscription.",
    })
  }
})

export type PlatformCommercialFiltersV1 = z.infer<
  typeof platformCommercialFiltersV1Schema
>
export type PlatformCommercialPageV1 = z.infer<
  typeof platformCommercialPageV1Schema
>
export type PlatformCommercialItemV1 = z.infer<
  typeof platformCommercialItemV1Schema
>
export type PlatformCommercialActionRequestV1 = z.infer<
  typeof platformCommercialActionRequestV1Schema
>
export type PlatformCommercialActionPreviewV1 = z.infer<
  typeof platformCommercialActionPreviewV1Schema
>
export type ExecutePlatformCommercialActionV1 = z.infer<
  typeof executePlatformCommercialActionV1Schema
>
